import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { apiClient } from '../utils/api';
import { useAuth } from './AuthContext';
import { Resume } from '../types';

interface ResumeContextType {
  resumes: Resume[];
  loading: boolean;
  fetchResumes: () => Promise<void>;
  refreshResumes: () => void;
}

const ResumeContext = createContext<ResumeContextType | undefined>(undefined);

export const useResumes = () => {
  const context = useContext(ResumeContext);
  if (context === undefined) {
    throw new Error('useResumes must be used within a ResumeProvider');
  }
  return context;
};

export const ResumeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();

  // Memoized fetch function that doesn't depend on user state
  const fetchResumes = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiClient.get<{ success: boolean; data: Resume[]; message: string }>('/resumes');
      setResumes(result.data || []);
    } catch (error) {
      console.error('Error fetching resumes:', error);
      setResumes([]);
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies - this function is stable

  // Effect that handles authentication-dependent fetching
  useEffect(() => {
    // Don't fetch if auth is still loading
    if (authLoading) {
      console.log('Auth still loading, skipping resumes fetch');
      return;
    }
    
    // Don't fetch if user is not authenticated
    if (!user) {
      console.log('No user authenticated, clearing resumes');
      setResumes([]);
      setLoading(false);
      return;
    }

    // Additional safety check: ensure we have a valid user with an ID
    if (!user.id) {
      console.log('User has no ID, skipping resumes fetch');
      setResumes([]);
      setLoading(false);
      return;
    }

    console.log('User authenticated, fetching resumes');
    // Fetch resumes when user is authenticated
    fetchResumes();
  }, [user, authLoading, fetchResumes]);

  const refreshResumes = useCallback(() => {
    fetchResumes();
  }, [fetchResumes]);

  return (
    <ResumeContext.Provider value={{
      resumes,
      loading,
      fetchResumes,
      refreshResumes
    }}>
      {children}
    </ResumeContext.Provider>
  );
}; 